import React, { useState, useEffect } from 'react';
import { db } from '../../../db/firebase';
import { collection, updateDoc, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Search, Package, Truck, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { cancelOrderAndRestoreStock } from '../../../db/firebaseOrders';

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showOrderDetails, setShowOrderDetails] = useState(false);
    const [showTrackingModal, setShowTrackingModal] = useState(false);
    const [trackingLink, setTrackingLink] = useState('');
    const [updatingOrderId, setUpdatingOrderId] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);

    // Fetch orders from Firebase
    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const ordersRef = collection(db, 'orders');
                const q = query(ordersRef, orderBy('timestamp', 'desc'));
                
                // Real-time listener for orders
                const unsubscribe = onSnapshot(q, (snapshot) => {
                    const ordersData = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    setOrders(ordersData);
                    setLoading(false);
                });

                return unsubscribe;
            } catch (error) {
                console.error('Error fetching orders:', error);
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);

    // Define valid status transitions
    const getValidNextStatuses = (currentStatus) => {
        const statusFlow = {
            'placed': ['confirmed', 'cancelled'],
            'confirmed': ['shipped', 'cancelled'],
            'shipped': ['completed', 'cancelled'],
            'completed': [], // Final state
            'cancelled': []  // Final state
        };
        
        return statusFlow[currentStatus] || [];
    };

    // Check if status can be changed
    const canChangeStatus = (currentStatus, newStatus) => {
        // If already in final state, no changes allowed
        if (['completed', 'cancelled'].includes(currentStatus)) {
            return false;
        }
        
        // Allow cancellation from any non-final state
        if (newStatus === 'cancelled') {
            return true;
        }
        
        // Check if the transition is valid according to status flow
        const validNextStatuses = getValidNextStatuses(currentStatus);
        return validNextStatuses.includes(newStatus);
    };

    // Update order status with confirmation
    const updateOrderStatus = async (orderId, newStatus, trackingNumber = '') => {
        const order = orders.find(o => o.id === orderId);
        const currentStatus = order?.status;

        if (!canChangeStatus(currentStatus, newStatus)) {
            alert('Invalid status transition. Please follow the proper order flow.');
            return;
        }

        try {
            setUpdatingOrderId(orderId);
            const orderRef = doc(db, 'orders', orderId);
            const updateData = {
                status: newStatus,
                updatedAt: new Date()
            };

            // Add tracking link if provided
            if (newStatus === 'shipped' && trackingNumber) {
                updateData.trackingLink = trackingNumber;
            }

            await updateDoc(orderRef, updateData);

            // If order is cancelled, restore stock
            if (newStatus === 'cancelled') {
                await cancelOrderAndRestoreStock(orderId);
            }

            // Close modals
            setShowTrackingModal(false);
            setShowConfirmModal(false);
            setTrackingLink('');
            setPendingStatusChange(null);
            
        } catch (error) {
            console.error('Error updating order status:', error);
            alert('Error updating order status: ' + error.message);
        } finally {
            setUpdatingOrderId(null);
        }
    };

    // Handle status change with confirmation
    const handleStatusChange = (orderId, newStatus) => {
        const order = orders.find(o => o.id === orderId);
        const currentStatus = order?.status;

        // Check if status can be changed
        if (!canChangeStatus(currentStatus, newStatus)) {
            alert('Invalid status transition. Please follow the proper order flow.');
            return;
        }

        // Show tracking modal for shipped status
        if (newStatus === 'shipped') {
            setPendingStatusChange({ orderId, newStatus });
            setShowTrackingModal(true);
            return;
        }

        // Show confirmation for ALL status changes except cancellation from placed
        if (newStatus === 'cancelled' && currentStatus !== 'placed') {
            setPendingStatusChange({ orderId, newStatus });
            setShowConfirmModal(true);
            return;
        }

        // Show confirmation for completed status
        if (newStatus === 'completed') {
            setPendingStatusChange({ orderId, newStatus });
            setShowConfirmModal(true);
            return;
        }

        // Direct update for other status changes (placed → confirmed, placed → cancelled)
        updateOrderStatus(orderId, newStatus);
    };

    // Confirm status change
    const confirmStatusChange = () => {
        if (pendingStatusChange) {
            const { orderId, newStatus } = pendingStatusChange;
            updateOrderStatus(orderId, newStatus, trackingLink);
        }
    };

    // Get available status options for a given order
    const getStatusOptions = (currentStatus) => {
        const allStatuses = [
            { value: 'placed', label: 'Placed' },
            { value: 'confirmed', label: 'Confirmed' },
            { value: 'shipped', label: 'Shipped' },
            { value: 'completed', label: 'Completed' },
            { value: 'cancelled', label: 'Cancelled' }
        ];

        // If in final state, return only current status
        if (['completed', 'cancelled'].includes(currentStatus)) {
            return allStatuses.filter(status => status.value === currentStatus);
        }

        // Return valid next statuses plus current status
        const validNextStatuses = getValidNextStatuses(currentStatus);
        const availableStatuses = [currentStatus, ...validNextStatuses];
        
        return allStatuses.filter(status => availableStatuses.includes(status.value));
    };

    // Filter orders based on search and status
    const filteredOrders = orders.filter(order => {
        const matchesSearch = 
            order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer?.email?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // Get status badge color
    const getStatusColor = (status) => {
        switch (status) {
            case 'placed':
                return 'bg-purple-100 text-purple-800';
            case 'confirmed':
                return 'bg-blue-100 text-blue-800';
            case 'shipped':
                return 'bg-yellow-100 text-yellow-800';
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'placed':
                return <Package size={16} />;
            case 'confirmed':
                return <Clock size={16} />;
            case 'shipped':
                return <Truck size={16} />;
            case 'completed':
                return <CheckCircle size={16} />;
            default:
                return <Package size={16} />;
        }
    };

    // Format date function
    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        
        try {
            let date;
            
            // Handle Firebase Timestamp objects
            if (dateValue.toDate) {
                date = dateValue.toDate();
            } 
            // Handle Firebase Timestamp with seconds property
            else if (dateValue.seconds) {
                date = new Date(dateValue.seconds * 1000);
            }
            // Handle string dates
            else if (typeof dateValue === 'string') {
                date = new Date(dateValue);
            }
            // Handle Date objects
            else if (dateValue instanceof Date) {
                date = dateValue;
            }
            // Fallback
            else {
                date = new Date(dateValue);
            }
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting date:', error, dateValue);
            return 'Date Error';
        }
    };

    // Calculate total items in order
    const calculateTotalItems = (order) => {
        return order.items?.reduce((total, item) => total + item.productQuantity, 0) || 0;
    };

    // View order details
    const viewOrderDetails = (order) => {
        setSelectedOrder(order);
        setShowOrderDetails(true);
    };

    // Get payment method display text
    const getPaymentMethodText = (paymentMethod) => {
        switch (paymentMethod) {
            case 'cod':
                return 'Cash on Delivery';
            case 'card':
                return 'Credit/Debit Card';
            case 'bank':
                return 'Bank Transfer';
            default:
                return paymentMethod || 'Cash on Delivery';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="h-24 bg-gray-300 rounded"></div>
                            ))}
                        </div>
                        <div className="space-y-4">
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="h-20 bg-gray-300 rounded"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-8 flex justify-between items-center">
                " "
            </div>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Orders Management</h1>
                    <p className="text-gray-600">Manage and track all customer orders</p>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                                <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
                            </div>
                            <div className="bg-blue-100 p-3 rounded-full">
                                <Package className="text-blue-600" size={24} />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Placed</p>
                                <p className="text-2xl font-bold text-purple-600">
                                    {orders.filter(o => o.status === 'placed').length}
                                </p>
                            </div>
                            <div className="bg-purple-100 p-3 rounded-full">
                                <Package className="text-purple-600" size={24} />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Confirmed</p>
                                <p className="text-2xl font-bold text-blue-600">
                                    {orders.filter(o => o.status === 'confirmed').length}
                                </p>
                            </div>
                            <div className="bg-blue-100 p-3 rounded-full">
                                <Clock className="text-blue-600" size={24} />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Shipped</p>
                                <p className="text-2xl font-bold text-yellow-600">
                                    {orders.filter(o => o.status === 'shipped').length}
                                </p>
                            </div>
                            <div className="bg-yellow-100 p-3 rounded-full">
                                <Truck className="text-yellow-600" size={24} />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Completed</p>
                                <p className="text-2xl font-bold text-green-600">
                                    {orders.filter(o => o.status === 'completed').length}
                                </p>
                            </div>
                            <div className="bg-green-100 p-3 rounded-full">
                                <CheckCircle className="text-green-600" size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                    <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                        <div className="flex-1 w-full md:w-auto">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Search orders by ID, name, or email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        
                        <div className="flex gap-4 w-full md:w-auto">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Status</option>
                                <option value="placed">Placed</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="shipped">Shipped</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Orders Table */}
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Order Details
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Customer
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Items & Total
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                            No orders found matching your criteria.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredOrders.map((order) => {
                                        const isFinalStatus = ['completed', 'cancelled'].includes(order.status);
                                        const statusOptions = getStatusOptions(order.status);
                                        
                                        return (
                                            <tr key={order.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {order.orderId}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {getPaymentMethodText(order.paymentMethod)}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {order.customer?.firstName} {order.customer?.lastName}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {order.customer?.email}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {order.customer?.phone}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {calculateTotalItems(order)} items
                                                        </div>
                                                        <div className="text-sm text-gray-900 font-semibold">
                                                            PKR {order.total?.toLocaleString()}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center space-x-2">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                                            {getStatusIcon(order.status)}
                                                            <span className="ml-1 capitalize">{order.status}</span>
                                                        </span>
                                                        {order.trackingLink && (
                                                            <a 
                                                                href={order.trackingLink} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:text-blue-800 text-xs"
                                                            >
                                                                Track
                                                            </a>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(order.timestamp || order.createdAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => viewOrderDetails(order)}
                                                            className="text-blue-600 hover:text-blue-900 transition-colors"
                                                        >
                                                            View
                                                        </button>
                                                        
                                                        {!isFinalStatus && (
                                                            <select
                                                                value={order.status}
                                                                onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                                                disabled={updatingOrderId === order.id}
                                                                className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                                                            >
                                                                {statusOptions.map(option => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                        {isFinalStatus && (
                                                            <span className="text-xs text-gray-500 italic">
                                                                Final
                                                            </span>
                                                        )}
                                                        {updatingOrderId === order.id && (
                                                            <span className="text-xs text-gray-500">Updating...</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Order Details Modal */}
                {showOrderDetails && selectedOrder && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-bold text-gray-900">
                                        Order Details - {selectedOrder.orderId}
                                    </h2>
                                    <button
                                        onClick={() => setShowOrderDetails(false)}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        ×
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Order Items */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
                                        <div className="space-y-4">
                                            {selectedOrder.items?.map((item, index) => (
                                                <div key={index} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                                                    <img
                                                        src={item.productImage}
                                                        alt={item.productName}
                                                        className="w-16 h-16 object-cover rounded border"
                                                        onError={(e) => {
                                                            e.target.src = '/placeholder-image.jpg';
                                                        }}
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-medium text-gray-900">
                                                            {item.productName}
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            Color: {item.productColor} | Size: {item.productSize} | Sole: {item.productSoleType || 'Leather'}
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            Quantity: {item.productQuantity}
                                                        </div>
                                                        {item.soleAdjustment !== 0 && (
                                                            <div className={`text-xs ${item.soleAdjustment < 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                Sole adjustment: {item.soleAdjustment > 0 ? '+' : ''}{item.soleAdjustment} PKR
                                                            </div>
                                                        )}
                                                        <div className="font-semibold text-gray-900">
                                                            PKR {((item.productDiscountPrice || item.productPrice) * item.productQuantity).toLocaleString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Order Information */}
                                    <div className="space-y-6">
                                        {/* Customer Info */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Information</h3>
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <p className="mb-1"><strong>Name:</strong> {selectedOrder.customer?.firstName} {selectedOrder.customer?.lastName}</p>
                                                <p className="mb-1"><strong>Email:</strong> {selectedOrder.customer?.email}</p>
                                                <p className="mb-1"><strong>Phone:</strong> {selectedOrder.customer?.phone}</p>
                                                {selectedOrder.customer?.alternatePhone && (
                                                    <p><strong>Alt Phone:</strong> {selectedOrder.customer?.alternatePhone}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Shipping Address */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Shipping Address</h3>
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <p className="mb-1">{selectedOrder.shippingAddress?.streetAddress}</p>
                                                {selectedOrder.shippingAddress?.apartment && (
                                                    <p className="mb-1">{selectedOrder.shippingAddress?.apartment}</p>
                                                )}
                                                <p className="mb-1">
                                                    {selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.state} {selectedOrder.shippingAddress?.zipCode}
                                                </p>
                                                <p className="mb-1">{selectedOrder.shippingAddress?.country}</p>
                                                {selectedOrder.shippingAddress?.landmark && (
                                                    <p><strong>Landmark:</strong> {selectedOrder.shippingAddress?.landmark}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Order Summary */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Order Summary</h3>
                                            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                                <div className="flex justify-between">
                                                    <span>Subtotal ({calculateTotalItems(selectedOrder)} items):</span>
                                                    <span>PKR {selectedOrder.subtotal?.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Shipping:</span>
                                                    <span className="text-green-600">Free</span>
                                                </div>
                                                <div className="flex justify-between border-t border-gray-300 pt-2 font-bold">
                                                    <span>Total:</span>
                                                    <span>PKR {selectedOrder.total?.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between text-sm text-gray-600">
                                                    <span>Payment Method:</span>
                                                    <span>{getPaymentMethodText(selectedOrder.paymentMethod)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Order Status */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Order Status</h3>
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <div className="flex items-center space-x-2 mb-2">
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedOrder.status)}`}>
                                                        {getStatusIcon(selectedOrder.status)}
                                                        <span className="ml-1 capitalize">{selectedOrder.status}</span>
                                                    </span>
                                                </div>
                                                {selectedOrder.trackingLink && (
                                                    <div className="mb-3">
                                                        <p className="text-sm font-medium text-gray-700 mb-1">Tracking Link:</p>
                                                        <a 
                                                            href={selectedOrder.trackingLink} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:text-blue-800 text-sm break-all"
                                                        >
                                                            {selectedOrder.trackingLink}
                                                        </a>
                                                    </div>
                                                )}
                                                {!['completed', 'cancelled'].includes(selectedOrder.status) && (
                                                    <select
                                                        value={selectedOrder.status}
                                                        onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    >
                                                        {getStatusOptions(selectedOrder.status).map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                                {['completed', 'cancelled'].includes(selectedOrder.status) && (
                                                    <p className="text-sm text-gray-500 italic">
                                                        This order has been {selectedOrder.status} and cannot be modified.
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Order Notes */}
                                        {selectedOrder.notes && (
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Order Notes</h3>
                                                <div className="bg-gray-50 p-4 rounded-lg">
                                                    <p className="text-gray-700">{selectedOrder.notes}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        onClick={() => setShowOrderDetails(false)}
                                        className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tracking Link Modal */}
                {showTrackingModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-md w-full p-6">
                            <div className="flex items-center space-x-2 mb-4">
                                <Truck className="text-yellow-600" size={24} />
                                <h3 className="text-lg font-semibold text-gray-900">Add Tracking Information</h3>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Please enter the tracking link for this shipment. This will be shared with the customer.
                            </p>
                            <input
                                type="text"
                                placeholder="https://tracking.courier.com/track/ABC123..."
                                value={trackingLink}
                                onChange={(e) => setTrackingLink(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                            />
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setShowTrackingModal(false);
                                        setTrackingLink('');
                                        setPendingStatusChange(null);
                                    }}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmStatusChange}
                                    disabled={!trackingLink.trim()}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Confirm Shipment
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Confirmation Modal */}
                {showConfirmModal && pendingStatusChange && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-md w-full p-6">
                            <div className="flex items-center space-x-2 mb-4">
                                <AlertCircle className="text-red-600" size={24} />
                                <h3 className="text-lg font-semibold text-gray-900">Confirm Status Change</h3>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Are you sure you want to change this order status to <strong>{pendingStatusChange.newStatus}</strong>?
                                {pendingStatusChange.newStatus === 'cancelled' && ' This will restore the product stock.'}
                                {pendingStatusChange.newStatus === 'completed' && ' This action cannot be undone.'}
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        setPendingStatusChange(null);
                                    }}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmStatusChange}
                                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Orders;